import { useState, useEffect, useRef, useCallback } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getUCNameLookup } from './ucLookupCache';

const useFirebaseData = (filters) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const prevFiltersRef = useRef(null);
  const isMountedRef = useRef(true);

  // Manual refetch function
  const refetch = useCallback(() => {
    prevFiltersRef.current = null; // Reset to force refetch
    setRefreshKey(prev => prev + 1);
  }, []);

  const district = filters?.district || '';
  const tahsil = filters?.tahsil || '';
  const uc = filters?.uc || '';
  const dogType = filters?.dogType || '';
  const startDate = filters?.startDate || '';
  const endDate = filters?.endDate || '';

  // Get UC lookup (uses shared cache)
  const getUCLookup = useCallback(() => getUCNameLookup(), []);

  // Process and filter records
  const processRecords = useCallback((querySnapshot, ucLookup, startDate, endDate) => {
    let records = [];

    querySnapshot.forEach((doc) => {
      const docData = doc.data();
      records.push({
        id: doc.id,
        district: docData.district,
        tahsil: docData.tahsil,
        dogType: docData.dogType,
        imageUrl: docData.imageUrl,
        status: docData.status,
        ucId: docData.ucId,
        uc: ucLookup[docData.ucId] || null,
        uploadedBy: docData.uploadedBy,
        timestamp: docData.timestamp,
        latitude: docData.coordinates?.lat,
        longitude: docData.coordinates?.lng,
        // Handle both Firestore Timestamp and regular timestamps
        date: docData.timestamp?.toDate?.() || (docData.timestamp instanceof Date ? docData.timestamp : (docData.timestamp ? new Date(docData.timestamp) : null))
      });
    });

    // Client-side date filtering
    if (startDate || endDate) {
      records = records.filter(record => {
        if (!record.date) return false;
        if (startDate && endDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          return record.date >= start && record.date <= end;
        } else if (startDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          return record.date >= start;
        } else if (endDate) {
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          return record.date <= end;
        }
        return true;
      });
    }

    // Sort by date (newest first)
    records.sort((a, b) => {
      if (!a.date) return 1;
      if (!b.date) return -1;
      return b.date - a.date;
    });

    return records;
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    let cancelled = false;

    const filterKey = JSON.stringify({ district, tahsil, uc, dogType, startDate, endDate });

    if (prevFiltersRef.current === filterKey) {
      return;
    }
    prevFiltersRef.current = filterKey;

    const fetchData = async () => {
      if (!cancelled && isMountedRef.current) {
        setLoading(true);
      }

      try {
        const ucLookup = await getUCLookup();
        if (cancelled) return; // Abort if a newer fetch has started

        // Build query with equality filters only (date filtering done client-side)
        const conditions = [];

        if (district) conditions.push(where('district', '==', district));
        if (tahsil) conditions.push(where('tahsil', '==', tahsil));
        if (uc) conditions.push(where('ucId', '==', uc));
        if (dogType) conditions.push(where('dogType', '==', dogType));

        const q = conditions.length > 0
          ? query(collection(db, 'DogCullingReports'), ...conditions)
          : query(collection(db, 'DogCullingReports'));

        const querySnapshot = await getDocs(q);
        if (cancelled) return; // Abort if a newer fetch has started

        const records = processRecords(querySnapshot, ucLookup, startDate, endDate);

        if (!cancelled && isMountedRef.current) {
          setData(records);
          setError(null);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        if (!cancelled && isMountedRef.current) {
          setError(err.message);
        }
      } finally {
        if (!cancelled && isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true; // Mark this fetch as stale so it cannot overwrite newer results
      isMountedRef.current = false;
    };
  }, [district, tahsil, uc, dogType, startDate, endDate, getUCLookup, processRecords, refreshKey]);

  return { data, loading, error, refetch };
};

export default useFirebaseData;
